import classNames from 'classnames';
import { format, parse } from 'date-fns';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';

import { CommonApi, PatientDTO, PeopleApi } from '../../api';
import { normalizePhoneNumber, patientFormValidation } from '../../utils';
import { Select } from '../atoms';
import { Modal } from '../molecules';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  patientId: number | null;
};

export const EditPatientModal: FC<Props> = ({ isOpen, onClose, patientId }) => {
  const queryClient = useQueryClient();

  const { data: patient, isLoading } = useQuery({
    queryKey: [PeopleApi.KEY, 'getPatientDetails', patientId],
    queryFn: () => PeopleApi.getPatientDetails(String(patientId!)),
    enabled: isOpen && !!patientId,
  });

  const { data: genderList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 107],
    queryFn: () => CommonApi.getReference(107),
    enabled: isOpen,
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<PatientDTO>({
    defaultValues: patient
      ? {
          ...patient,
          gender_id: patient.gender_id,
          username: patient.phone,
          email:
            patient.contacts.find((contact) => contact.contact_type.id === 19)
              ?.value || '',
          birthdate: format(new Date(patient.birthdate), 'dd-MM-yyyy'),
        }
      : {},
  });

  useEffect(() => {
    if (patient && isOpen) {
      reset({
        ...patient,
        gender_id: patient.gender_id,
        username: patient.phone,
        email:
          patient.contacts.find((contact) => contact.contact_type.id === 19)
            ?.value || '',
        birthdate: format(new Date(patient.birthdate), 'dd-MM-yyyy'),
      });
    }
  }, [patient, isOpen, reset]);

  const { mutate: updatePatient, isLoading: isPending } = useMutation({
    mutationFn: (updatedData: PatientDTO) =>
      PeopleApi.updatePatient(String(patientId!), updatedData),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [PeopleApi.KEY] });
      queryClient.invalidateQueries([
        PeopleApi.KEY,
        'getPatientDetails',
        patientId,
      ]);
      toast.success('Данные пациента успешно обновлены');
      reset();
      onClose();
    },
    onError(error) {
      console.error(error);
      toast.error('Ошибка! Проверьте данные');
    },
  });

  const onSubmit: SubmitHandler<PatientDTO> = useCallback(
    async (data) => {
      if (!patient) return;

      const updatedContacts = (patient.contacts || []).map((contact) => {
        if (contact.contact_type.id === 18) {
          return { ...contact, value: normalizePhoneNumber(data.username) };
        }
        if (contact.contact_type.id === 19) {
          return { ...contact, value: data.email };
        }
        return contact;
      });

      let formattedBirthdate = data.birthdate;
      if (data.birthdate?.trim()) {
        try {
          formattedBirthdate = format(
            parse(data.birthdate, 'dd-MM-yyyy', new Date()),
            'yyyy-MM-dd',
          );
        } catch {
          toast.error('Неверный формат даты');
          return;
        }
      }

      updatePatient({
        ...data,
        username: normalizePhoneNumber(data.username),
        birthdate: formattedBirthdate,
        contacts: updatedContacts,
        documents: patient.documents || [],
      });
    },
    [updatePatient, patient],
  );

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="flex max-h-[90vh] min-h-[300px] flex-col overflow-hidden rounded-[16px] bg-white p-[24px]">
          <div className="flex items-center justify-center py-[80px]">
            <div className="text-[#151515]">Загрузка...</div>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex max-h-[90vh] min-h-[300px] flex-col overflow-hidden rounded-[16px] bg-white p-[24px]">
        <h2 className="mb-[60px] text-2xl font-bold text-[#151515]">
          Редактировать пациента
        </h2>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex grow flex-col overflow-auto"
        >
          <div className="mb-[24px] grid grid-cols-1 gap-[12px] sm:grid-cols-2">
            <div>
              <input
                {...register('surname', patientFormValidation.surname)}
                className={classNames(
                  'input',
                  errors.surname && 'input--invalid',
                )}
                name="surname"
                placeholder="Фамилия"
                type="text"
              />
              {errors.surname && (
                <p className="text-red-500 mt-[10px] text-sm">
                  {errors.surname.message}
                </p>
              )}
            </div>
            <div>
              <input
                {...register('name', patientFormValidation.name)}
                className={classNames('input', errors.name && 'input--invalid')}
                name="name"
                placeholder="Имя"
                type="text"
              />
              {errors.name && (
                <p className="text-red-500 mt-[10px] text-sm">
                  {errors.name.message}
                </p>
              )}
            </div>
            <div>
              <input
                {...register('midname', patientFormValidation.midname)}
                className={classNames(
                  'input',
                  errors.midname && 'input--invalid',
                )}
                name="midname"
                placeholder="Отчество"
                type="text"
              />
              {errors.midname && (
                <p className="text-red-500 mt-[10px] text-sm">
                  {errors.midname.message}
                </p>
              )}
            </div>
            <div>
              <Controller
                control={control}
                name="gender_id"
                rules={patientFormValidation.gender_id}
                render={({ field, fieldState }) => (
                  <Select
                    error={fieldState.error}
                    onChange={field.onChange}
                    options={genderList.map((gender) => ({
                      label: gender.value,
                      value: gender.id,
                    }))}
                    placeholder="Пол"
                    value={field.value}
                  />
                )}
              />
              {errors.gender_id && (
                <p className="text-red-500 mt-[10px] text-sm">
                  {errors.gender_id.message}
                </p>
              )}
            </div>
            <div>
              <Controller
                control={control}
                name="username"
                rules={patientFormValidation.username}
                render={({ field, fieldState }) => (
                  <InputMask
                    {...field}
                    alwaysShowMask={false}
                    className={classNames(
                      'input',
                      fieldState.invalid && 'input--invalid',
                    )}
                    mask="+7 999 999 99 99"
                    maskPlaceholder={null}
                    type="tel"
                    value={field.value ?? ''}
                    placeholder="Телефон"
                  />
                )}
              />
              {errors.username && (
                <p className="text-red-500 mt-[10px] text-sm">
                  {errors.username.message}
                </p>
              )}
            </div>
            <div>
              <input
                {...register('email', patientFormValidation.email)}
                className={classNames(
                  'input',
                  errors.email && 'input--invalid',
                )}
                name="email"
                placeholder="Электронная почта"
                type="email"
              />
              {errors.email && (
                <p className="text-red-500 mt-[10px] text-sm">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div>
              <Controller
                control={control}
                name="birthdate"
                rules={patientFormValidation.birthdate}
                render={({ field, fieldState }) => (
                  <InputMask
                    {...field}
                    alwaysShowMask={false}
                    className={classNames(
                      'input',
                      fieldState.invalid && 'input--invalid',
                    )}
                    mask="99-99-9999"
                    maskPlaceholder={null}
                    placeholder="Дата рождения (ДД-ММ-ГГГГ)"
                    type="tel"
                    value={field.value ?? ''}
                  />
                )}
              />
              {errors.birthdate && (
                <p className="text-red-500 mt-[10px] text-sm">
                  {errors.birthdate.message}
                </p>
              )}
            </div>
          </div>

          <div className="mt-[32px] flex gap-[40px]">
            <button
              type="button"
              onClick={onClose}
              className="button--secondary button flex-1"
            >
              Отмена
            </button>
            <button
              className="button button--primary flex-1"
              type="submit"
              disabled={isPending}
            >
              {isPending ? 'Сохранение...' : 'Сохранить изменения'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
